import {
    BarChart3,
    CheckCircle,
    Filter, Home, MapPin,
    Plus, Search,
    Settings,
    Wrench,
    XCircle
} from 'lucide-react';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import UnitManagementModal from './UnitManagementModal';

const getUnitTypeText = (unitType) => {
  const normalized = String(unitType || '').trim().toLowerCase();
  const typeMap = {
    studio: '원룸',
    one_room: '1.5룸',
    '1room': '원룸',
    '1룸': '원룸',
    onehalf_room: '1.5룸',
    '1.5room': '1.5룸',
    '1.5룸': '1.5룸',
    two_room: '투룸',
    '2room': '투룸',
    '2룸': '투룸',
    투룸: '투룸',
    three_room: '쓰리룸 이상',
    four_room: '쓰리룸 이상',
    '3room': '쓰리룸 이상',
    '3룸': '쓰리룸 이상',
    '3룸이상': '쓰리룸 이상',
    '쓰리룸 이상': '쓰리룸 이상',
  };

  return typeMap[normalized] || unitType || '-';
};

const Units = () => {
  const navigate = useNavigate();
  const { buildingId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [selectedUnitForManagement, setSelectedUnitForManagement] = useState(null);
  const [showManagementModal, setShowManagementModal] = useState(false);

  // 건물 정보 가져오기
  const { data: building } = useQuery(['building', buildingId], () =>
    api.get(`/buildings/${buildingId}`)
  );

  // 세대 목록 가져오기
  const { data: unitsData, isLoading } = useQuery(['units', buildingId], () =>
    api.get(`/buildings/${buildingId}/units`)
  );
  
  const units = unitsData?.units || [];

  // 선택된 세대의 통계 정보 가져오기
  const { data: unitStats } = useQuery(
    ['unit-stats', selectedUnit?.id],
    () => api.get(`/units/${selectedUnit.id}/statistics`),
    { enabled: !!selectedUnit && showStatistics }
  );

  // 검색 및 필터링
  const filteredUnits = units.filter(unit => {
    const unitTypeText = getUnitTypeText(unit.unit_type).toLowerCase();
    const matchesSearch = unit.unit_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         unit.unit_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         unitTypeText.includes(searchTerm.toLowerCase()) ||
                         (unit.building_name || building?.name)?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || unit.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'occupied':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'reserved':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available':
        return '임대 가능';
      case 'occupied':
        return '임대중';
      case 'maintenance':
        return '정비중';
      case 'reserved':
        return '예약중';
      default:
        return '알 수 없음';
    }
  };

  const getConditionColor = (grade) => {
    switch (grade) {
      case 'A':
        return 'text-green-600';
      case 'B':
        return 'text-blue-600';
      case 'C':
        return 'text-yellow-600';
      case 'D':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">세대 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            {building?.name} - 세대 현황 및 관리
          </p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={() => setShowStatistics(!showStatistics)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            통계 보기
          </button>
          <button 
            onClick={() => navigate(`/buildings/${buildingId}/units/add`)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="mr-2 h-4 w-4" />
            세대 추가
          </button>
        </div>
      </div>

      {/* 전체 통계 카드 */}
      {showStatistics && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Home className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">총 세대 수</dt>
                    <dd className="text-lg font-medium text-gray-900">{units.length || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">임대중</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {units.filter(unit => unit.status === 'occupied').length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircle className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">임대 가능</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {units.filter(unit => unit.status === 'available').length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Wrench className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">정비중</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {units.filter(unit => unit.status === 'maintenance').length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* 검색 */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="건물명, 호수 또는 세대 타입으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          {/* 상태 필터 */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">전체 상태</option>
              <option value="available">임대 가능</option>
              <option value="occupied">임대중</option>
              <option value="maintenance">정비중</option>
              <option value="reserved">예약중</option>
            </select>
          </div>

          {/* 통계 */}
          <div className="text-right">
            <p className="text-sm text-gray-500">총 {filteredUnits.length}개 세대</p>
          </div>
        </div>
      </div>

      {/* 세대 목록 */}
      <div className="grid grid-cols-2 gap-3 md:gap-6 lg:grid-cols-3">
        {filteredUnits.map((unit) => (
          <div key={unit.id} className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            {/* 세대 헤더 */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-3 py-3 md:px-6 md:py-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-white md:text-lg">{unit.building_name || building?.name}</h3>
                  <p className="text-xs text-primary-100 md:text-sm">{unit.unit_number}호</p>
                </div>
                <span className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium md:px-2.5 md:text-xs ${getStatusColor(unit.status)}`}>
                  {getStatusText(unit.status)}
                </span>
              </div>
              <div className="mt-2 flex items-center truncate text-xs text-primary-100 md:text-sm">
                <MapPin className="mr-1 h-3 w-3 shrink-0 md:h-4 md:w-4" />
                {unit.floor}층
                {unit.area && (
                  <>
                    <span className="mx-1 md:mx-2">•</span>
                    {unit.area}㎡
                  </>
                )}
              </div>
            </div>

            {/* 세대 정보 */}
            <div className="p-3 md:p-6">
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <div className="rounded-md bg-gray-50 p-2">
                  <span className="block text-[11px] text-gray-500 md:text-sm">타입</span>
                  <span className="block truncate text-xs font-semibold text-gray-900 md:text-sm">{getUnitTypeText(unit.unit_type)}</span>
                </div>

                {unit.condition_grade && (
                  <div className="rounded-md bg-gray-50 p-2">
                    <span className="block text-[11px] text-gray-500 md:text-sm">상태</span>
                    <span className={`block text-xs font-semibold md:text-sm ${getConditionColor(unit.condition_grade)}`}>
                      {unit.condition_grade}등급
                    </span>
                  </div>
                )}

                {unit.standard_monthly_rent && (
                  <div className="rounded-md bg-gray-50 p-2">
                    <span className="block text-[11px] text-gray-500 md:text-sm">월세</span>
                    <span className="block truncate text-xs font-semibold text-gray-900 md:text-sm">{unit.standard_monthly_rent.toLocaleString()}원</span>
                  </div>
                )}

                {unit.standard_deposit && (
                  <div className="rounded-md bg-gray-50 p-2">
                    <span className="block text-[11px] text-gray-500 md:text-sm">보증금</span>
                    <span className="block truncate text-xs font-semibold text-gray-900 md:text-sm">{unit.standard_deposit.toLocaleString()}원</span>
                  </div>
                )}

                {unit.standard_maintenance_fee && (
                  <div className="rounded-md bg-gray-50 p-2">
                    <span className="block text-[11px] text-gray-500 md:text-sm">관리비</span>
                    <span className="block truncate text-xs font-semibold text-gray-900 md:text-sm">{unit.standard_maintenance_fee.toLocaleString()}원</span>
                  </div>
                )}
              </div>

              {/* 액션 버튼 */}
              <div className="mt-3 grid grid-cols-2 gap-2 md:mt-6">
                <button 
                  onClick={() => {
                    setSelectedUnit(unit);
                    setShowStatistics(true);
                  }}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 md:px-3 md:py-2 md:text-sm"
                >
                  <BarChart3 className="mr-1 h-3 w-3 md:h-4 md:w-4" />
                  통계
                </button>
                <button 
                  onClick={() => {
                    setSelectedUnitForManagement(unit);
                    setShowManagementModal(true);
                  }}
                  className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-2 py-1.5 text-xs font-medium leading-4 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 md:px-3 md:py-2 md:text-sm"
                >
                  <Settings className="mr-1 h-3 w-3 md:h-4 md:w-4" />
                  상세정보
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 선택된 세대의 상세 통계 */}
      {selectedUnit && showStatistics && unitStats && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedUnit.unit_number} - 상세 통계
            </h2>
            <button 
              onClick={() => setSelectedUnit(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">세대 정보</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">호수</span>
                  <span className="font-medium">{unitStats.unit_info.unit_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">층수</span>
                  <span className="font-medium">{unitStats.unit_info.floor}층</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">면적</span>
                  <span className="font-medium">{unitStats.unit_info.area}㎡</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">기준월세</span>
                  <span className="font-medium">
                    {unitStats.unit_info.standard_monthly_rent ? `${unitStats.unit_info.standard_monthly_rent.toLocaleString()}원` : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">기준보증금</span>
                  <span className="font-medium">
                    {unitStats.unit_info.standard_deposit ? `${unitStats.unit_info.standard_deposit.toLocaleString()}원` : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">기준관리비</span>
                  <span className="font-medium">
                    {unitStats.unit_info.standard_maintenance_fee ? `${unitStats.unit_info.standard_maintenance_fee.toLocaleString()}원` : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">상태</span>
                  <span className="font-medium">{unitStats.unit_info.status}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">점검 현황</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">총 점검</span>
                  <span className="font-medium">{unitStats.inspection_statistics.total_inspections}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">이번 달</span>
                  <span className="font-medium text-blue-600">{unitStats.inspection_statistics.recent_inspections}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">최근 점검</span>
                  <span className="font-medium text-sm">
                    {unitStats.inspection_statistics.latest_inspection.date 
                      ? new Date(unitStats.inspection_statistics.latest_inspection.date).toLocaleDateString()
                      : '없음'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">시설 현황</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">총 시설</span>
                  <span className="font-medium">{unitStats.facility_statistics.total_facilities}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">정상 운영</span>
                  <span className="font-medium text-green-600">{unitStats.facility_statistics.operational_facilities}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">정비 중</span>
                  <span className="font-medium text-yellow-600">{unitStats.facility_statistics.maintenance_facilities}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">운영율</span>
                  <span className="font-medium">{unitStats.facility_statistics.operational_rate}%</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">재정 정보</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">월세</span>
                  <span className="font-medium">{unitStats.financial_info.monthly_rent?.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">보증금</span>
                  <span className="font-medium">{unitStats.financial_info.deposit?.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">관리비</span>
                  <span className="font-medium">{unitStats.financial_info.maintenance_fee?.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium text-gray-600">임대 상태</span>
                  <span className={`font-bold ${unitStats.financial_info.is_rented ? 'text-green-600' : 'text-blue-600'}`}>
                    {unitStats.financial_info.is_rented ? '임대중' : '빈 세대'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 빈 상태 */}
      {filteredUnits.length === 0 && (
        <div className="text-center py-12">
          <Home className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">세대가 없습니다</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterStatus !== 'all' 
              ? '검색 조건에 맞는 세대가 없습니다.' 
              : '첫 번째 세대를 추가해보세요.'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <div className="mt-6">
              <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                <Plus className="mr-2 h-4 w-4" />
                세대 추가
              </button>
            </div>
          )}
        </div>
      )}

      {/* 세대 관리 모달 */}
      <UnitManagementModal
        unit={selectedUnitForManagement}
        buildingId={buildingId}
        isOpen={showManagementModal}
        onClose={() => {
          setShowManagementModal(false);
          setSelectedUnitForManagement(null);
        }}
        onUnitUpdated={() => {
          // 세대 정보가 업데이트되면 목록을 새로고침
        }}
      />
    </div>
  );
};

export default Units;
